import { useMediaQuery } from '@nexelem/deprecated-theme';
import { IconComponentProps } from '@nexelem/icons';
import { Breadcrumbs } from '@nexelem/breadcrumbs';
import { Button } from '@nexelem/components';
import { FC, memo, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledMuiAppBar } from './components/StyledMuiAppBar';
import { StyledToolbar } from './components/StyledToolbar';
import { NotificationIcon } from './notificationIcon/NotificationIcon';
import clsx from 'clsx';
import { ProfileButton, User } from './ProfileButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faRightFromBracket } from '@fortawesome/pro-light-svg-icons';

export type AppBarProps<T> = {
  readonly hasMenu?: boolean;
  readonly MenuIcon: FC<IconComponentProps>;
  readonly LogoIcon: FC<IconComponentProps>;
  readonly NotificationIcon: FC<IconComponentProps>;
  readonly avatar?: string;
  readonly user: T;
  readonly returnButton: ReactNode;
  readonly notificationsAmount?: number;
  readonly onMenuClick?: () => void;
  readonly onNotificationClick?: () => void;
  readonly isJumbo?: boolean;
  readonly onLogout?: () => void;
  readonly onProfileClick?: () => void;
};

export const AppBar = memo(function <T extends User>({
  avatar,
  user,
  returnButton,
  onMenuClick,
  hasMenu = true,
  LogoIcon,
  MenuIcon,
  onLogout = () => null,
  isJumbo = false,
  onProfileClick = () => null,
}: AppBarProps<T>) {
  const isAvatarOnly = useMediaQuery('(max-width: 768px)');
  const isMobileBreadcrumbs = useMediaQuery('(max-width: 540px)');
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  return (
    <StyledMuiAppBar
      className={clsx(isJumbo ? 'py-2' : 'py-0.75')}
      id="appbar"
      isJumbo={isJumbo}
      position="absolute"
      ref={ref}
    >
      <StyledToolbar disableGutters withBreadcrumbs>
        {hasMenu && <MenuIcon className="menu-icon" onClick={onMenuClick} />}
        <LogoIcon />
        {isMobileBreadcrumbs && (
          <Button className="ml-4" StartIcon={<FontAwesomeIcon icon={faAngleLeft} />} variant="text">
            {returnButton}
          </Button>
        )}
        {!isMobileBreadcrumbs && <Breadcrumbs />}
      </StyledToolbar>
      <StyledToolbar disableGutters>
        <NotificationIcon className="notification-button" />
        <ProfileButton avatarOnly={isAvatarOnly} className="mr-1" src={avatar} user={user} onClick={onProfileClick} />
        <Button
          size={isJumbo ? 'jumbo' : 'medium'}
          StartIcon={<FontAwesomeIcon icon={faRightFromBracket} />}
          variant="text"
          onClick={onLogout}
        >
          {t('routing.logout')}
        </Button>
      </StyledToolbar>
    </StyledMuiAppBar>
  );
});

AppBar.displayName = 'AppBar';
